import { useState } from "react";
import type { ActivityRow, Channel } from "@shared/types";
import { formatClockTime } from "@/lib/format";

interface ActivityFeedProps {
  rows: ActivityRow[];
  loading?: boolean;
}

function channelGlyph(channel: Channel): string {
  switch (channel) {
    case "whatsapp":
      return "W";
    case "voice":
      return "V";
    case "web":
      return "M";
  }
}

function channelLabel(channel: Channel): string {
  return channel === "whatsapp" ? "WhatsApp" : channel === "voice" ? "Voice" : "Web";
}

function outcomeClass(outcome: string): string {
  const lower = outcome.toLowerCase();
  if (lower.includes("escal")) return "activity-outcome activity-outcome--escalated";
  if (lower.includes("order")) return "activity-outcome activity-outcome--ordered";
  return "activity-outcome";
}

function isEscalated(row: ActivityRow): boolean {
  return (
    row.outcome.toLowerCase().includes("escal") ||
    typeof row.escalationReason === "string"
  );
}

export function ActivityFeed({ rows, loading = false }: ActivityFeedProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading && rows.length === 0) {
    return (
      <div className="activity" data-testid="activity-feed">
        <div className="activity-empty">Loading activity…</div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="activity" data-testid="activity-feed">
        <div className="activity-empty">No activity yet.</div>
      </div>
    );
  }

  return (
    <div className="activity" data-testid="activity-feed">
      {rows.map((row) => {
        const escalated = isEscalated(row);
        const expanded = expandedId === row.id;
        const name = row.customerDisplayName ?? "Customer";
        return (
          <div key={row.id}>
            <div className="activity-row" data-testid={`activity-row-${row.id}`}>
              <span className="activity-time">{formatClockTime(row.ts)}</span>
              <span
                className="activity-channel"
                title={channelLabel(row.channel)}
                aria-label={channelLabel(row.channel)}
              >
                {channelGlyph(row.channel)}
              </span>
              <span className="activity-name">
                {name}
                {row.customerLast4 && (
                  <span className="activity-name-last4">
                    ({row.customerLast4})
                  </span>
                )}
              </span>
              <span className={outcomeClass(row.outcome)}>
                {row.outcome}
                {escalated && row.escalationReason && (
                  <button
                    type="button"
                    className="activity-why"
                    onClick={() =>
                      setExpandedId(expanded ? null : row.id)
                    }
                    aria-expanded={expanded}
                    data-testid={`activity-why-${row.id}`}
                  >
                    Why?
                  </button>
                )}
              </span>
            </div>
            {expanded && row.escalationReason && (
              <div
                className="activity-reason"
                data-testid={`activity-reason-${row.id}`}
              >
                {row.escalationReason}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
